import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock, MapPin } from "lucide-react";
import { api } from "../api";
import { useLang } from "../i18n";
import { Countdown } from "../components/Countdown";
import { Lineup } from "../components/Lineup";
import { VipSection } from "../components/VipSection";
import { Reveal, SectionHead } from "../components/Section";
import { FAQS } from "../data/site";

export default function EventPage() {
    const { t, lang } = useLang();
    const [event, setEvent] = useState(null);
    const faqs = FAQS[lang] || FAQS.en;

    useEffect(() => {
        api.get("/event").then((r) => {
            setEvent(r.data.event);
        }).catch(() => {});
    }, []);

    return (
        <div data-testid="event-page" className="pt-32 pb-24">
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <SectionHead eyebrow="The first featured event" title={event?.name || "LAKOU — THE EXPERIENCE"} testId="event-title" />
                <Reveal delay={0.1}>
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="rounded-2xl border border-lakou-gold/20 bg-lakou-coal/70 p-6 flex items-start gap-4" data-testid="event-date-card">
                            <CalendarDays size={20} className="text-lakou-gold shrink-0 mt-0.5" />
                            <div>
                                <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-lakou-sand">{t("event_date")}</div>
                                <div className="mt-1 font-display font-bold text-lakou-cream">Saturday, December 12, 2026</div>
                                <div className="text-xs text-lakou-grey mt-1 flex items-center gap-1"><Clock size={11} /> Starts 7:30 PM</div>
                            </div>
                        </div>
                        <div className="rounded-2xl border border-lakou-gold/20 bg-lakou-coal/70 p-6 flex items-start gap-4" data-testid="event-venue-card">
                            <MapPin size={20} className="text-lakou-gold shrink-0 mt-0.5" />
                            <div>
                                <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-lakou-sand">{t("event_location")}</div>
                                <div className="mt-1 font-display font-bold text-lakou-cream">{event?.city || "Léogâne, Haiti"}</div>
                                <div className="text-xs text-lakou-grey mt-1">{event?.venue || "Donpee Guest House — 7 Route de Mathieu"}</div>
                            </div>
                        </div>
                        <div className="rounded-2xl border border-lakou-gold/20 bg-lakou-coal/70 p-6 flex flex-col justify-between gap-4" data-testid="event-tickets-card">
                            <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-lakou-sand">{t("tickets_live")}</div>
                            <Link to="/tickets" data-testid="event-get-tickets"
                                className="inline-flex items-center justify-center gap-2 rounded-full bg-lakou-orange hover:bg-lakou-gold text-white hover:text-lakou-ink font-display font-bold text-xs uppercase tracking-[0.15em] px-6 py-3.5 transition-colors duration-300">
                                {t("nav_cta")} <ArrowRight size={14} />
                            </Link>
                        </div>
                    </div>
                </Reveal>
                <Reveal delay={0.15}>
                    <p className="mt-10 max-w-3xl text-lakou-grey leading-relaxed" data-testid="event-description">
                        {event?.description}
                    </p>
                    <p className="mt-4 text-xs text-lakou-sand">{event?.venue_note}</p>
                </Reveal>
                <Reveal delay={0.2} className="mt-12">
                    <Countdown />
                </Reveal>
            </section>

            <Lineup />

            <VipSection />

            <section className="max-w-3xl mx-auto px-4 sm:px-6 mt-0">
                <SectionHead eyebrow={t("faq_eyebrow")} title={t("faq_title")} center testId="faq-title" />
                <div className="mt-10 space-y-4">
                    {faqs.map((f, i) => (
                        <Reveal key={i} delay={i * 0.05}>
                            <details className="group rounded-2xl border border-lakou-gold/15 bg-lakou-coal/60 px-6 py-5 open:border-lakou-gold/40 transition-colors" data-testid={`faq-item-${i}`}>
                                <summary className="cursor-pointer list-none font-display font-bold text-lakou-cream flex items-center justify-between gap-4">
                                    {f.q}
                                    <span className="text-lakou-gold group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                                </summary>
                                <p className="mt-3 text-sm text-lakou-grey leading-relaxed">{f.a}</p>
                            </details>
                        </Reveal>
                    ))}
                </div>
            </section>
        </div>
    );
}
